import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  getPlatformSettings,
  fetchPlatformSettings,
  getFairSeedConfig,
} from "../lib/platformSettings";
import { ensureCsrfToken } from "../lib/csrf";
import { formatMoney } from "../utils/formatters";
import { CapabilityGate } from "../access/AccessContext";
import { PERMISSIONS } from "../access/capabilities";

import ExecutiveSection from "../components/executive/ExecutiveSection";

const threatCapabilityOptions = [
  { value: 1, label: "1 - Opportunistic" },
  { value: 2, label: "2 - Basic" },
  { value: 3, label: "3 - Moderate" },
  { value: 4, label: "4 - Organized" },
  { value: 5, label: "5 - Advanced Commercial" },
  { value: 6, label: "6 - Persistent" },
  { value: 7, label: "7 - Targeted Advanced" },
  { value: 8, label: "8 - Specialized Offensive" },
  { value: 9, label: "9 - Strategic Elite" },
  { value: 10, label: "10 - Exceptional" },
];

const controlStrengthOptions = [
  { value: 1, label: "1 - Minimal Controls" },
  { value: 2, label: "2 - Basic Controls" },
  { value: 3, label: "3 - Standard Controls" },
  { value: 4, label: "4 - Managed Controls" },
  { value: 5, label: "5 - Mature Controls" },
  { value: 6, label: "6 - Strong Controls" },
  { value: 7, label: "7 - Advanced Controls" },
  { value: 8, label: "8 - Optimized Controls" },
  { value: 9, label: "9 - Adaptive Controls" },
  { value: 10, label: "10 - Best-in-Class Controls" },
];

export default function FairScenarios() {
  const [scenarios, setScenarios] = useState([]);
  const [status, setStatus] = useState("Active");
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState(null);
  const [settings, setSettings] = useState(getPlatformSettings());
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [restrictedContext, setRestrictedContext] = useState([]);
  const previewRequestId = useRef(0);

  axios.defaults.withCredentials = true;
  axios.defaults.xsrfCookieName = "csrftoken";
  axios.defaults.xsrfHeaderName = "X-CSRFToken";

  useEffect(() => {
    let isActive = true;

    const loadSettings = async () => {
      try {
        const freshSettings = await fetchPlatformSettings();

        if (!isActive) return;

        setSettings(freshSettings);
      } catch (err) {
        console.error(
          "Error loading platform settings:",
          err
        );
      } finally {
        if (isActive) {
          setSettingsLoaded(true);
        }
      }
    };

    loadSettings();

    return () => {
      isActive = false;
    };
  }, []);

  const calculateScenarioQuality = () => {
    if (!form) {
      return null;
    }

    let score = 100;

    const warnings = [];

    // ------------------------
    // FAIR Validation
    // ------------------------

    if (
      form.probability_of_action < 0 ||
      form.probability_of_action > 1
    ) {
      score -= 20;

      warnings.push(
        "Probability of Action should be between 0 and 1."
      );
    }

    if (
      form.secondary_loss_probability < 0 ||
      form.secondary_loss_probability > 1
    ) {
      score -= 15;

      warnings.push(
        "Secondary Loss Probability should be between 0 and 1."
      );
    }

    // ------------------------
    // Distribution Validation
    // ------------------------

    if (
      form.primary_loss_min >
      form.primary_loss_most_likely
    ) {
      score -= 15;

      warnings.push(
        "Primary Loss Min exceeds Most Likely."
      );
    }

    if (
      form.primary_loss_most_likely >
      form.primary_loss_max
    ) {
      score -= 15;

      warnings.push(
        "Primary Loss Most Likely exceeds Max."
      );
    }

    // ------------------------
    // Governance Validation
    // ------------------------

    if (!form.reviewer) {
      score -= 10;

      warnings.push(
        "Reviewer not assigned."
      );
    }

    if (!form.assumptions) {
      score -= 10;

      warnings.push(
        "Scenario assumptions missing."
      );
    }

    if (form.confidence_level === "Low") {
      score -= 10;

      warnings.push(
        "Scenario confidence level is Low."
      );
    }

    // ------------------------
    // Review Staleness
    // ------------------------

    if (form.last_review_date) {

      const reviewDate =
        new Date(form.last_review_date);

      const now = new Date();

      const diffDays =
        (now - reviewDate) /
        (1000 * 60 * 60 * 24);

      if (diffDays > 180) {

        score -= 10;

        warnings.push(
          "Scenario review is older than 180 days."
        );
      }
    }

    score = Math.max(0, score);

    return {
      score,
      warnings,
      ready: score >= 75,
    };
  };

  const quality = calculateScenarioQuality();

  const showMessage = (type, text) => {

    setMessage({
      type,
      text,
    });

    setTimeout(() => {
      setMessage(null);
    }, 4000);
  };

  useEffect(() => {
    if (!settingsLoaded) return;

    fetchScenarios();
  }, [settingsLoaded, status]);

  
  useEffect(() => {
    if (!settingsLoaded) return;
    if (!form) return;

    const requestId = ++previewRequestId.current;
    setPreview(null);
    setPreviewLoading(true);

    const timeout = setTimeout(async () => {
      try {
        const previewPayload = {
          ...form,
          simulations: Number(settings.simulations || 5000),
          seed: getFairSeedConfig(settings).seed,
        };

        const res = await axios.post(
          "/api/digital-risk/dashboard/fair-scenarios/preview/",
          previewPayload
        );

        if (requestId === previewRequestId.current) {
          setPreview(res.data);
        }
      } catch (err) {
        console.error(err);
        if (requestId === previewRequestId.current) {
          setPreview(null);
        }
      } finally {
        if (requestId === previewRequestId.current) {
          setPreviewLoading(false);
        }
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    settingsLoaded,
    form,
    settings.simulations,
    settings.seedMode,
    settings.seedValue,
  ]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await axios.get(
          "/api/digital-risk/dashboard/fair-scenario-options/"
        );
        setOptions(res.data);
      } catch (error) {
        handleDependentLoadError(error, "scenario options");
      }
    };

    fetchOptions();
  }, []);

  const fetchScenarios = async () => {
    try {
      const res = await axios.get(
        "/api/digital-risk/fair-scenarios/",
        {
          params: {
            status,
            simulations: Number(settings.simulations || 5000),
            seed: getFairSeedConfig(settings).seed,
          },
        }
      );
      setScenarios(res.data);
      setLoadError(null);
    } catch (error) {
      const authorizationDenied = [401, 403].includes(error.response?.status);
      setLoadError(authorizationDenied
        ? "You do not have permission to view Risk Scenarios."
        : "Risk Scenarios could not be loaded. Please retry or contact a platform administrator.");
    }
  };

  const handleDependentLoadError = (error, source) => {
    if ([401, 403].includes(error.response?.status)) {
      setRestrictedContext((current) => current.includes(source) ? current : [...current, source]);
      return;
    }
    console.error(`Error loading Risk Scenarios ${source}:`, error);
  };

  const loadScenario = async (id) => {
    const res = await axios.get(
      `/api/digital-risk/dashboard/fair-scenarios/${id}/`
    );

    const rawDependencies =
      (res.data.dependencies || []).map(Number);

    const validDependencyIds =
      dependencyOptions.length > 0
        ? rawDependencies.filter((id) =>
            dependencyOptions.some(
              (dep) => Number(dep.id) === Number(id)
            )
          )
        : rawDependencies;

    setSelectedId(id);

    setForm({
      ...res.data,
      dependencies: validDependencyIds,
    });
  };


  const createScenario = async () => {

    try {

      const defaultIncidentType =
        options.incident_types?.[0] || "Ransomware";

      const defaultAsset =
        typeof options.assets?.[0] === "string"
          ? options.assets[0]
          : options.assets?.[0]?.name || "Generic Asset";

      const payload = {
        name: "New Scenario",
        incident_type: defaultIncidentType,
        asset_name: defaultAsset,
        status: "Draft",

        contact_frequency: 1,
        probability_of_action: 0.1,

        threat_capability: 5,
        control_strength: 5,

        primary_loss_min: 0,
        primary_loss_most_likely: 0,
        primary_loss_max: 0,

        secondary_loss_probability: 0,

        secondary_loss_min: 0,
        secondary_loss_most_likely: 0,
        secondary_loss_max: 0,

        confidence_level: "Medium",
        estimation_method: "Expert Judgement",

        data_source: "",
        assumptions: "",
        reviewer: "",
      };

      const res = await axios.post(
        "/api/digital-risk/fair-scenarios/",
        payload
      );

      await fetchScenarios();

      await loadScenario(res.data.id);

    } catch (error) {

      console.error(
        error.response?.data || error
      );
  
    }
  };


  const saveScenario = async () => {
    if (!selectedId || !form) return;

    try {
      const {
        tef,
        lef,
        vulnerability,
        dependency_details,
        risk_dependency_details,
        created_at,
        ...cleanForm
      } = form;

      const payload = {
        ...cleanForm,
        status: "Draft",
        risk_dependencies: (form.risk_dependencies || []).map(Number),
      };

      await ensureCsrfToken();

      const res = await axios.put(
        `/api/digital-risk/fair-scenarios/${selectedId}/`,
        payload,
        { withCredentials: true }
      );

      setForm(res.data);
      await fetchScenarios();

      showMessage("success", "Scenario draft saved.");
    } catch (error) {
      console.error(
        "Save scenario error:",
        error.response?.data || error
      );

      showMessage(
        "error",
        "Unable to save scenario."
      );
    }
  };

  const archiveScenario = async (id) => {
    try {
      await axios.post(
        `/api/digital-risk/dashboard/fair-scenarios/${id}/archive/`
      );

      await fetchScenarios();

      if (selectedId === id) {
        setSelectedId(null);
        setForm(null);
      }
    } catch (error) {
      console.error(error.response?.data || error);
      alert("Unable to archive scenario.");
    }
  };

  const activateScenario = async (id = selectedId) => {
    if (!id || !form) return;


    const qualityCheck = calculateScenarioQuality();
      if (!qualityCheck?.ready) {
        showMessage(
          "warning",
          "Scenario is not ready for activation."
        );
        return;
      }

    try {
      const payload = {
        ...form,
        status: "Active",
      };

      await ensureCsrfToken();

      const res = await axios.put(
        `/api/digital-risk/fair-scenarios/${id}/`,
        payload,
        { withCredentials: true }
      );

      setForm(res.data);
      await fetchScenarios();

      localStorage.setItem("fair-scenarios-version", String(Date.now()));
      window.dispatchEvent(
        new CustomEvent("fair-scenarios-changed", {
          detail: {
            scenarioId: id,
            status: "Active",
          },
        })
      );

      showMessage(
        "success",
        "Scenario activated successfully."
      );
    } catch (error) {
      console.error("Activate scenario error:", error.response?.data || error);

      showMessage(
        "error",
        error.response?.data?.detail || "Unable to activate scenario."
      );
    }
  };

  const update = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const [options, setOptions] = useState({
    incident_types: [],
    assets: [],
  });

  const [assetOptions, setAssetOptions] = useState([]);

  useEffect(() => {
    axios
      .get("/api/digital-risk/dashboard/asset-options/")
      .then((res) => setAssetOptions(res.data))
      .catch((error) => handleDependentLoadError(error, "asset options"));
  }, []);

  const [showDependencies, setShowDependencies] = useState(false);
  const [dependencyOptions, setDependencyOptions] = useState([]);

  useEffect(() => {
    const loadDependencies = async () => {
      try {
        const res = await axios.get("/api/digital-risk/risk-dependencies/");
        setDependencyOptions(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        handleDependentLoadError(error, "risk dependencies");
      }
    };

    loadDependencies();
  }, []);

  return (
    <div className="p-6 lg:flex lg:h-full lg:min-h-0 lg:flex-col print:!block print:!h-auto">

      {loadError && <div role="alert" className="shrink-0 mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950"><strong>Access restricted.</strong> {loadError}</div>}
      {!loadError && restrictedContext.length > 0 && <div role="status" className="shrink-0 mb-6 rounded-xl border border-slate-300 bg-slate-50 p-4 text-slate-700">Some editing context is unavailable for your current capabilities: {restrictedContext.join(", ")}. Scenario visibility remains available.</div>}

      <div className="shrink-0">
        <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
            Scenario Modeling
        </p>

        <h1 className="text-2xl font-semibold text-slate-900 mt-2">
          Risk Scenarios
        </h1>

        <p className="text-slate-500 mt-2 max-w-3xl mt-1">
           Define and govern digital risk scenarios used across FAIR-based risk analytics.
        </p>

      </div>



    <div className="grid grid-cols-12 mt-6 gap-6 lg:flex-1 lg:min-h-0 lg:grid-rows-[minmax(0,1fr)] print:!block">
      {/* LEFT: LIST */}
      <ExecutiveSection
        title="Scenario Library"
        subtitle="Governed FAIR scenarios available for quantitative analysis."
         className="col-span-12 lg:col-span-4 w-full min-w-0 lg:min-h-0 lg:grid lg:grid-rows-[auto_minmax(0,1fr)] print:!block"
      >

        <div className="lg:flex lg:min-h-0 lg:flex-col print:!block">
        <div className="flex shrink-0 justify-between mb-4">
          <h2 className="font-semibold">FAIR Scenarios</h2>
          <CapabilityGate permission={PERMISSIONS.fair.add}><button
            onClick={createScenario}
            className="bg-black text-white px-3 py-1 rounded"
          >
            New
          </button></CapabilityGate>
        </div>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="shrink-0 self-start mb-4 border px-2 py-1 rounded"
        >
          <option>Active</option>
          <option>Draft</option>
          <option>Archived</option>
          <option>All</option>
        </select>

        <div className="space-y-2 lg:flex-1 lg:min-h-0 lg:overflow-auto print:!overflow-visible">
          {scenarios.map((s) => (
            
          <div
            key={s.id}
            onClick={() => loadScenario(s.id)}
            className={`rounded-2xl border p-4 cursor-pointer transition ${
              selectedId === s.id
                ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-sm leading-tight">
                  {s.name}
                </div>

                <div
                  className={`text-xs mt-1 ${
                    selectedId === s.id ? "text-slate-300" : "text-slate-500"
                  }`}
                >
                  {s.incident_type} | {s.asset_name}
                </div>
              </div>

              <span
                className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                  s.status === "Active"
                    ? selectedId === s.id
                      ? "bg-green-400/20 text-green-200"
                      : "bg-green-100 text-green-700"
                    : s.status === "Draft"
                    ? selectedId === s.id
                      ? "bg-amber-400/20 text-amber-200"
                      : "bg-amber-100 text-amber-700"
                    : selectedId === s.id
                    ? "bg-slate-400/20 text-slate-200"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {s.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              <div>
                <div className={selectedId === s.id ? "text-slate-400" : "text-slate-500"}>
                  Incident
                </div>
                <div className="font-medium truncate">
                  {s.incident_type}
                </div>
              </div>

              <div>
                <div className={selectedId === s.id ? "text-slate-400" : "text-slate-500"}>
                  Asset
                </div>
                <div className="font-medium truncate">
                  {s.asset_name}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4 text-xs">
              {s.status !== "Archived" && <CapabilityGate permission={PERMISSIONS.fair.delete}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    archiveScenario(s.id);
                  }}
                  className={`px-2 py-1 rounded-lg border ${
                    selectedId === s.id
                      ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                      : "border-slate-300 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  Archive
                </button>
              </CapabilityGate>}

              {s.status === "Archived" && <CapabilityGate permission={PERMISSIONS.fair.change}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    activateScenario(s.id);
                  }}
                  className={`px-2 py-1 rounded-lg border ${
                    selectedId === s.id
                      ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                      : "border-slate-300 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  Activate
                </button>
              </CapabilityGate>}
            </div>
          </div>


          ))}
        </div>
        </div>
      </ExecutiveSection>


      {/* RIGHT: FORM */}
      <ExecutiveSection
        title="Scenario Modeling Workspace"
        subtitle="Configure FAIR scenario assumptions, governance metadata and loss distributions."
        className="col-span-12 lg:col-span-8 w-full min-w-0 lg:min-h-0 lg:grid lg:grid-rows-[auto_minmax(0,1fr)] print:!block"
      >

        <div className="lg:min-h-0 lg:overflow-auto print:!overflow-visible">
        {message && (

          <div
            className={`
              mb-4
              px-4
              py-3
              rounded-xl
              text-sm
              font-medium
              shadow-sm

              ${
                message.type === "success"
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : message.type === "error"
                  ? "bg-red-100 text-red-800 border border-red-200"
                  : "bg-amber-100 text-amber-800 border border-amber-200"
              }
            `}
          >
            {message.text}
          </div>
        )}
        


        {quality && (
          <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">
                  Scenario Quality & Readiness
                </h3>
                <p className="text-xs text-gray-500">
                  Governance validation for FAIR model activation.
                </p>
              </div>

              <div
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  quality.ready
                    ? "bg-green-100 text-green-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {quality.ready ? "Ready" : "Needs Review"}
              </div>
            </div>

            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Quality Score</span>
                <span>{quality.score}%</span>
              </div>

              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    quality.score >= 75
                      ? "bg-green-500"
                      : quality.score >= 50
                      ? "bg-amber-500"
                      : "bg-red-500"
                  }`}
                  style={{ width: `${quality.score}%` }}
                />
              </div>
            </div>

            {quality.warnings.length > 0 ? (
              <div className="space-y-2">
                {quality.warnings.map((warning, index) => (
                  <div
                    key={index}
                    className="text-xs text-gray-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                  >
                    {warning}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                Scenario passes all governance quality checks.
              </div>
            )}
          </div>
        )}


        {form && (
          <div className="mb-4 rounded-2xl border border-gray-200 bg-slate-950 text-white p-4">
            <div className="mb-4">
              <h3 className="font-semibold">
                Live FAIR Metrics
              </h3>
              <p className="text-xs text-slate-400">
                Real-time quantitative outputs based on current scenario inputs.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <PreviewCard
                title="TEF"
                value={previewLoading ? "Loading…" : preview ? Number(preview.tef).toFixed(2) : "-"}
              />

              <PreviewCard
                title="Vulnerability"
                value={previewLoading ? "Loading…" : preview ? `${(preview.vulnerability * 100).toFixed(1)}%` : "-"}
              />

              <PreviewCard
                title="LEF"
                value={previewLoading ? "Loading…" : preview ? Number(preview.lef).toFixed(2) : "-"}
              />

              <PreviewCard
                title="Estimated EAL"
                value={previewLoading ? "Loading…" : preview ? formatMoney(preview.expected_annual_loss, settings.currency) : "-"}
              />
            </div>
          </div>
        )}



        {!form ? (
          <div>Select a scenario</div>
        ) : (
          <div className="space-y-4">

            
            <div className="pt-4 border-t">
                <h3 className="font-semibold mb-3">FAIR Models Inputs</h3>

                <Field label="Scenario Name">
                    <input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-left"
                    placeholder="Name"
                    />
                </Field>

                <Field label="Incident Type">
                  <select
                    value={form.incident_type || ""}
                    onChange={(e) => update("incident_type", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-left"
                  >
                    {options.incident_types.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Asset">
                  <select
                    value={form.asset || ""}
                    onChange={(e) =>
                      setForm({ ...form, asset: Number(e.target.value) })
                    }
                    className="w-full border rounded px-2 py-1"
                  >
                    <option value="">Select asset</option>

                    {assetOptions.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Risk Dependencies & Correlation Drivers">
                  <div className="border border-gray-200 rounded-lg bg-white">
                    <button
                      type="button"
                      onClick={() => setShowDependencies(!showDependencies)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span>
                        {
                          dependencyOptions.filter((dep) =>
                            (form.risk_dependencies || [])
                              .map(Number)
                              .includes(Number(dep.id))
                          ).length
                        } selected
                      </span>

                      <span className="text-xs text-gray-500">
                        {showDependencies ? "Hide" : "Manage"}
                      </span>
                    </button>

                    {showDependencies && (
                      <div className="border-t border-gray-100 p-3 space-y-2 max-h-48 overflow-y-auto">
                        {dependencyOptions.map((dep) => {
                          const selectedIds = (form.risk_dependencies || []).map(Number);
                          const depId = Number(dep.id);

                          return (
                            <label
                              key={dep.id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(depId)}
                                onChange={(e) => {
                                  update(
                                    "risk_dependencies",
                                    e.target.checked
                                      ? [...selectedIds, depId]
                                      : selectedIds.filter((id) => id !== depId)
                                  );
                                }}
                              />

                              <span>
                                {dep.dependency_type_name} — {dep.name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Field>

                <Field label="Contact Frequency (CF)">
                    <input
                    type="number"
                    value={form.contact_frequency}
                    onChange={(e) =>
                        update("contact_frequency", Number(e.target.value))
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
                    placeholder="Contact Frequency"
                    />
                </Field>

                <Field label="Probability of Action (PoA)">
                    <input
                    type="number"
                    value={form.probability_of_action}
                    onChange={(e) =>
                        update("probability_of_action", Number(e.target.value))
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
                    placeholder="Probability of Action"
                    />
                </Field>

                <CalculatedField
                  label="Threat Event Frequency = CF × PoA"
                  value={previewLoading ? "Loading…" : preview ? Number(preview.tef).toFixed(2) : "-"}
                />
                <Field label="Threat Capability">
                    <select
                      value={form.threat_capability}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          threat_capability: Number(e.target.value),
                        })
                      }
                    //  className="border rounded-xl px-4 py-3 w-full"
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-left"
                    >
                      {threatCapabilityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                </Field>

                <Field label="Control Strength">
                    <select
                      value={form.control_strength}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          control_strength: Number(e.target.value),
                        })
                      }
                      //className="border rounded-xl px-4 py-3 w-full"
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-left"
                    >
                      {controlStrengthOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                </Field>

               <CalculatedField
                    label="Vulnerability"
                    value={
                        preview && !previewLoading
                        ? (preview.vulnerability * 100).toFixed(1) + "%"
                        : "-"
                    }
                />
            </div>

            <div className="pt-4 border-t">
                <h3 className="font-semibold mb-3">Loss Magnitude</h3>

                <div className="flex ml-4 mt-2 items-center gap-6 text-xs text-gray-500">
                    <span className="w-64"></span>
                        <div className="grid grid-cols-3 gap-2 w-[420px] text-center">
                            <span>Min</span>
                            <span>Most likely</span>
                            <span>Max</span>
                        </div>
                    </div>
                <TripleField
                    label="Primary Loss"
                    min={form.primary_loss_min}
                    likely={form.primary_loss_most_likely}
                    max={form.primary_loss_max}
                    onMin={(v) => update("primary_loss_min", v)}
                    onLikely={(v) => update("primary_loss_most_likely", v)}
                    onMax={(v) => update("primary_loss_max", v)}
                />

                <Field label="Secondary Loss Probability">
                    <input
                        type="number"
                        step="0.01"
                        value={form.secondary_loss_probability ?? 0}
                        onChange={(e) =>
                        update("secondary_loss_probability", Number(e.target.value))
                        }
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
                    />
                </Field>

                <TripleField
                    label="Secondary Loss"
                    min={form.secondary_loss_min}
                    likely={form.secondary_loss_most_likely}
                    max={form.secondary_loss_max}
                    onMin={(v) => update("secondary_loss_min", v)}
                    onLikely={(v) => update("secondary_loss_most_likely", v)}
                    onMax={(v) => update("secondary_loss_max", v)}
                />

            </div>


            <div className="pt-4 border-t">
            <h3 className="font-semibold mb-3">Model Governance</h3>

            <Field label="Status">
                <select
                value={form.status || "Draft"}
                onChange={(e) => update("status", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
                </select>
            </Field>

            <Field label="Confidence Level">
                <select
                value={form.confidence_level || "Medium"}
                onChange={(e) => update("confidence_level", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                </select>
            </Field>

            <Field label="Estimation Method">
                <select
                value={form.estimation_method || "Expert Judgement"}
                onChange={(e) => update("estimation_method", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                <option value="Expert Judgement">Expert Judgement</option>
                <option value="Historical Data">Historical Data</option>
                <option value="External Benchmark">External Benchmark</option>
                <option value="Hybrid">Hybrid</option>
                </select>
            </Field>

            <Field label="Reviewer">
                <input
                value={form.reviewer || ""}
                onChange={(e) => update("reviewer", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                />
            </Field>

            <Field label="Last Review Date">
                <input
                type="date"
                value={form.last_review_date || ""}
                onChange={(e) => update("last_review_date", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                />
            </Field>

            <Field label="Data Source">
                <textarea
                value={form.data_source || ""}
                onChange={(e) => update("data_source", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                rows={2}
                />
            </Field>

            <Field label="Assumptions">
                <textarea
                value={form.assumptions || ""}
                onChange={(e) => update("assumptions", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                rows={2}
                />
            </Field>
            </div>

            <CapabilityGate permission={PERMISSIONS.fair.change}><button
              onClick={saveScenario}
              className="bg-black text-white px-4 py-2 mt-3 rounded"
            >
              Save Draft
            </button></CapabilityGate>

            <CapabilityGate permission={PERMISSIONS.fair.change}><button
                onClick={async () => {
                  await saveScenario();
                  await activateScenario(selectedId);
                  await fetchScenarios();
                  const res = await axios.get(
                    `/api/digital-risk/dashboard/fair-scenarios/${selectedId}/`
                  );
                  setForm(res.data);
                }}
                className="bg-black text-white px-4 py-2 ml-4 rounded"
            >
                Activate Scenario
            </button></CapabilityGate>

          </div>
        )}

        </div>
      </ExecutiveSection>

    </div>
   </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex ml-4 mt-2 items-center gap-6 text-sm">
      <span className="text-gray-600 w-64">{label}</span>
      <div className="w-64">{children}</div>
    </div>
  );
}

function CalculatedField({ label, value }) {
  return (
    <div className="flex ml-4 mt-2 items-center gap-6 text-sm">
      <span className="text-gray-500 w-64">{label}</span>
      <div className="w-60 text-right font-semibold text-blue-600">
        {value}
      </div>
    </div>
  );
}

function PreviewCard({ title, value }) {
  return (
    <div className="border rounded-xl p-3 bg-slate-50">
      <p className="text-xs text-gray-500">{title}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function TripleField({ label, min, likely, max, onMin, onLikely, onMax }) {
  return (
    <div className="flex ml-4 mt-2 items-center gap-6 text-sm">
      <span className="text-gray-600 w-64">{label}</span>

      <div className="flex gap-2 w-[420px]">
        <input
          type="number"
          value={min ?? 0}
          onChange={(e) => onMin(Number(e.target.value))}
          className="w-1/3 border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
          placeholder="Min"
        />

        <input
          type="number"
          value={likely ?? 0}
          onChange={(e) => onLikely(Number(e.target.value))}
          className="w-1/3 border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
          placeholder="Likely"
        />

        <input
          type="number"
          value={max ?? 0}
          onChange={(e) => onMax(Number(e.target.value))}
          className="w-1/3 border border-gray-300 rounded-md px-2 py-1 text-sm text-right"
          placeholder="Max"
        />
      </div>
    </div>
  );
}
